/**
 *
 * @module samsungOrsayPlayerAdapter
 * @desc PlayerAdapter for Samsung Orsay
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://github.bamtech.co/fed-ce/hbo-appletv-app/blob/master/source/js/app/App.SecureKeyDelivery.js#L241
 * @see http://developer.samsung.com/tv/develop/legacy-platform-library/API00005/Player_172
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class SamsungOrsayPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 *
 */
export default class SamsungOrsayPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer
     * @throws {InvalidArgumentException}
     * @note The SamsungOrsayPlayerAdapter requires nativePlayer.Play
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'SamsungOrsayPlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        Check.assert.function(Util.getSafe(() => nativePlayer.Play), errorMessage.nativePlayer, InvalidArgumentException); // eslint-disable-line

        super(nativePlayer);

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.streamingProperty = null;

        /**
         *
         * @access public
         * @type {Object}
         * @desc Exposed listeners for when application developer needs to call
         * nativePlayer.setListener after a playbackSession.prepare
         * @since 3.0.0
         *
         */
        this.playerListener = null;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.currentPlayhead = null;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @throws {InvalidStateException} Needs valid accessToken
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'SamsungOrsayPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs valid accessToken`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        const { accessToken } = this;

        return new Promise((resolve, reject) => {

            if (Check.assigned(accessToken)) {

                const streamingProperty = `|COOKIE={Authorization=${accessToken}}|COMPONENT=HLS`;
                const url = `${playlistUri}${streamingProperty}`;

                this.playlistUri = url;
                this.streamingProperty = streamingProperty;

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.accessToken)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead or bitrate data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'SamsungOrsayPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            },
            get currentBitrate() {
                return `${this.method} unable to get NativePlayer bitrate data`;
            }
        };

        const { nativePlayer, unavailableMetric } = this;

        if (Check.not.function(nativePlayer.OnCurrentPlayTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        if (Check.not.function(nativePlayer.getCurrentBitrates)) {

            const reasons = [new ErrorReason('', errorMessage.currentBitrate)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = Math.floor(nativePlayer.getCurrentBitrates() / 1000) || unavailableMetric;
        const currentPlayhead = Math.floor(this.currentPlayhead / 1000) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     * @todo find a better event for onPlay (documentation does not contain anything that applies)
     * @todo find a better event for onPause (documentation does not contain anything that applies)
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'SamsungOrsayPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer, boundHandlers } = this;
        const { onPlay, onError, onPlayedToCompletion } = this;
        const { onCurrentPlayTime } = this;

        return new Promise((resolve, reject) => {

            if (Check.function(nativePlayer.OnStreamInfoReady)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);
                boundHandlers.onCurrentPlayTime = onCurrentPlayTime.bind(this);

                const playerListener = {
                    onbufferingstart: boundHandlers.onPlay,
                    onerror: boundHandlers.onError,
                    onstreamcompleted: boundHandlers.onPlayedToCompletion,
                    oncurrentplaytime: boundHandlers.onCurrentPlayTime
                };

                this.playerListener = playerListener;

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'SamsungOrsayPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer } = this;

        return new Promise((resolve) => {

            if (Check.function(nativePlayer.OnStreamInfoReady) && Check.equal(this.listener, listener)) {

                this.listener = null;
                this.playerListener = null;

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'SamsungOrsayPlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        this.drmProvider = drmProvider;

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener } = this;

        this.playlistUri = '';
        this.boundHandlers = {};
        this.currentPlayhead = null;
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 2.0.0
     * @param {Number} currentTime
     * @returns {Promise<Void>}
     *
     */
    onCurrentPlayTime(currentTime) {

        return new Promise((resolve) => {

            this.currentPlayhead = currentTime;

            return resolve();

        });

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/samsungOrsayPlayerAdapter.js